import { Link } from "react-router-dom";
import { Leaf, CheckCircle2, Users, Award, Sprout, ArrowRight } from "lucide-react";

const values = [
  {
    icon: Leaf,
    title: "Respekt for naturen",
    text: "Vi arbejder med skarpt værktøj og skånsomme metoder — så din hæk trives efter klipningen.",
  },
  {
    icon: Award,
    title: "Håndværk i højsædet",
    text: "Vi klipper efter linjen og har øje for detaljen. Resultatet skal både være smukt og holdbart.",
  },
  {
    icon: Users,
    title: "Ærlig dialog",
    text: "Fast pris, klar aftale og punktlig aflevering. Ingen skjulte gebyrer, ingen dårlige overraskelser.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-16">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-4">Om Syddansk Group</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-brand-text text-balance">
            Fra én ung mand med sin fars hækklipper — <span className="italic text-brand-primary">til et helt team</span>.
          </h1>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">
            Syv år senere leverer vi stadig havepleje med den samme passion, som det hele startede med. Kvalitet, ærlighed og hårdt arbejde er vores fundament.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="rounded-[2rem] overflow-hidden aspect-[4/3] border border-brand-border lg:sticky lg:top-28">
            <img
              src="https://images.pexels.com/photos/1105019/pexels-photo-1105019.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Havemand i arbejde"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="bg-brand-surface rounded-[2rem] border border-brand-border p-8 lg:p-12">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-muted">Vores historie</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 tracking-tight text-brand-text">
              Det hele startede med én hækklipper og en drøm.
            </h2>
            <div className="mt-6 space-y-5 text-brand-muted leading-relaxed">
              <p>
                Syddansk Group begyndte med en ung mand på blot 18 år, der havde en stor passion for grønne udearealer og velplejede omgivelser. Med sin fars hækklipper gik han i gang med at klippe hække for naboerne og tjene en lille skilling. Hver en krone blev geninvesteret i virksomheden, og efterhånden blev de første professionelle håndholdte maskiner en realitet.
              </p>
              <p>
                De første tre år var langt fra nemme. Der var udfordringer, modgang og bump på vejen, men viljestyrke, hårdt arbejde og modet til altid at tage det næste skridt gjorde forskellen. Drømmen var større end forhindringerne.
              </p>
              <p>
                I dag – syv år efter opstarten – er Syddansk Group vokset til en virksomhed i udvikling. Det, der begyndte med én person og en hækklipper, er blevet til et stærkt team af engagerede medarbejdere, som alle deler den samme passion for kvalitet og godt håndværk.
              </p>
              <p>
                Vi går op i detaljerne og leverer løsninger med præcision, omhu og stolthed. Uanset opgavens størrelse er vores mål altid det samme: at skabe velholdte udearealer og levere et resultat, som både vi og vores kunder kan være stolte af.
              </p>
              <p>
                Hos Syddansk Group tror vi på, at godt arbejde, ærlighed og stærke relationer er fundamentet for en virksomhed, der kan vokse — og det er netop de værdier, vi bygger videre på hver eneste dag.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-brand-border/60 grid grid-cols-3 gap-4">
              <div>
                <div className="font-display text-3xl text-brand-primary">7+</div>
                <div className="text-xs text-brand-muted uppercase tracking-widest mt-1">År</div>
              </div>
              <div>
                <div className="font-display text-3xl text-brand-primary">500+</div>
                <div className="text-xs text-brand-muted uppercase tracking-widest mt-1">Haver</div>
              </div>
              <div>
                <div className="font-display text-3xl text-brand-primary">4.9</div>
                <div className="text-xs text-brand-muted uppercase tracking-widest mt-1">Score</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">Vores værdier</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text max-w-2xl">
          Tre grundsten der bærer <span className="italic">alt</span> hvad vi gør.
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {values.map((v, i) => (
            <div
              key={i}
              data-testid={`value-${i}`}
              className="rounded-3xl border border-brand-border bg-brand-surface p-8"
            >
              <div className="h-12 w-12 rounded-2xl bg-brand-primary text-brand-bg grid place-items-center">
                <v.icon className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-xl mt-6 text-brand-text">{v.title}</h3>
              <p className="mt-2 text-brand-muted text-sm leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <div className="rounded-[2.5rem] bg-brand-soft border border-brand-border p-10 lg:p-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-brand-text">
                Skal vi tage os af din have i år?
              </h2>
              <p className="mt-4 text-brand-muted">
                Vi laver en gratis vurdering og giver dig en fast pris — helt uforpligtende.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/kontakt"
                data-testid="about-cta-contact"
                className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-7 py-3.5 font-medium hover:bg-brand-hover"
              >
                Få et tilbud <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/priser"
                data-testid="about-cta-pricing"
                className="inline-flex items-center gap-2 rounded-full border border-brand-primary text-brand-primary px-7 py-3.5 font-medium hover:bg-brand-primary/5"
              >
                Se priser
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
